import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getArticle, createArticle, updateArticle } from "../../api/articlesApi";
import { translateAuthError, API_ORIGIN } from "../../api/authApi";
import RichTextEditor from "../../components/RichTextEditor";
import FootnotesEditor from "../../components/FootnotesEditor";
import AdminImageField from "../../components/AdminImageField";
import MetadataFieldsEditor from "../../components/MetadataFieldsEditor";
import { EMPTY_METADATA, normalizeMetadata } from "../../utils/metadata";

// Section C (admin-only): shared create/edit form. Presence of the :id
// route param decides the mode. Every text field has an independent
// Persian and English half — either half can be left entirely empty (a
// piece with no translation yet), but a non-empty title needs its body
// filled in too, same language side only (see usecase.validArticleHalf
// on the backend).
function ArticleFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [titleFa, setTitleFa] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [bodyFa, setBodyFa] = useState("");
  const [bodyEn, setBodyEn] = useState("");
  const [footnotesFa, setFootnotesFa] = useState([]);
  const [footnotesEn, setFootnotesEn] = useState([]);
  const [file, setFile] = useState(null);
  const [existingImagePath, setExistingImagePath] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [metadata, setMetadata] = useState(EMPTY_METADATA);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!isEdit) return;
    getArticle(id)
      .then((a) => {
        setTitleFa(a.title_fa || "");
        setTitleEn(a.title_en || "");
        setBodyFa(a.body_fa || "");
        setBodyEn(a.body_en || "");
        setFootnotesFa(a.footnotes_fa || []);
        setFootnotesEn(a.footnotes_en || []);
        setExistingImagePath(a.image_path || "");
        setMetadata(normalizeMetadata(a.metadata));
      })
      .catch((err) => setError(translateAuthError(err.message)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    if (!titleFa.trim() && !titleEn.trim()) {
      setError("حداقل یکی از دو زبان باید عنوان و متن داشته باشد.");
      return;
    }
    if (Boolean(titleFa.trim()) !== Boolean(bodyFa.trim())) {
      setError("عنوان و متن فارسی باید همزمان پر یا خالی باشند.");
      return;
    }
    if (Boolean(titleEn.trim()) !== Boolean(bodyEn.trim())) {
      setError("عنوان و متن انگلیسی باید همزمان پر یا خالی باشند.");
      return;
    }

    const formData = new FormData();
    formData.append("title_fa", titleFa);
    formData.append("title_en", titleEn);
    formData.append("body_fa", bodyFa);
    formData.append("body_en", bodyEn);
    for (const note of footnotesFa.map((f) => f.trim()).filter(Boolean)) {
      formData.append("footnotes_fa", note);
    }
    for (const note of footnotesEn.map((f) => f.trim()).filter(Boolean)) {
      formData.append("footnotes_en", note);
    }
    formData.append("metadata", JSON.stringify(metadata));
    if (file) {
      formData.append("image", file);
    } else if (removeImage) {
      formData.append("remove_image", "true");
    }

    try {
      if (isEdit) {
        await updateArticle(token, id, formData);
      } else {
        await createArticle(token, formData);
      }
      navigate("/admin/articles");
    } catch (err) {
      setError(translateAuthError(err.message));
    }
  }

  return (
    <div className="admin-page">
      <Link to="/admin/articles" className="back-link">
        ← بازگشت به فهرست مقاله‌ها
      </Link>

      {loading ? (
        <p>در حال بارگذاری...</p>
      ) : (
        <form className="auth-card admin-article-form" onSubmit={handleSubmit}>
          <span className="corner corner-tr" />
          <span className="corner corner-tl" />
          <span className="corner corner-br" />
          <span className="corner corner-bl" />

          <h2>{isEdit ? "ویرایش مقاله" : "مقالهٔ جدید"}</h2>

          {error && <p className="error">{error}</p>}

          <div className="bilingual-section">
            <h3 className="bilingual-section-heading">فارسی</h3>

            <div className="auth-field">
              <label htmlFor="title-fa">عنوان</label>
              <input id="title-fa" type="text" value={titleFa} onChange={(e) => setTitleFa(e.target.value)} dir="rtl" />
            </div>

            <div className="auth-field">
              <label htmlFor="body-fa">متن</label>
              <RichTextEditor id="body-fa" value={bodyFa} onChange={setBodyFa} />
            </div>

            <div className="auth-field">
              <label>پانویس‌ها (اختیاری)</label>
              <FootnotesEditor footnotes={footnotesFa} onChange={setFootnotesFa} />
            </div>
          </div>

          <div className="bilingual-section">
            <h3 className="bilingual-section-heading">English</h3>

            <div className="auth-field">
              <label htmlFor="title-en">Title</label>
              <input id="title-en" type="text" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} dir="ltr" />
            </div>

            <div className="auth-field">
              <label htmlFor="body-en">Body</label>
              <RichTextEditor id="body-en" value={bodyEn} onChange={setBodyEn} />
            </div>

            <div className="auth-field">
              <label>Footnotes (optional)</label>
              <FootnotesEditor footnotes={footnotesEn} onChange={setFootnotesEn} />
            </div>
          </div>

          <AdminImageField
            label="تصویر کارت (اختیاری)"
            imageUrl={existingImagePath ? `${API_ORIGIN}${existingImagePath}` : ""}
            file={file}
            removed={removeImage}
            onFileChange={(chosen) => {
              setFile(chosen);
              if (chosen) setRemoveImage(false);
            }}
            onRemove={() => setRemoveImage(true)}
          />

          <div className="auth-field">
            <label>شناسه (اختیاری)</label>
            <MetadataFieldsEditor metadata={metadata} onChange={setMetadata} />
          </div>

          <button type="submit" className="auth-submit">
            {isEdit ? "ذخیرهٔ تغییرات" : "انتشار مقاله"}
          </button>
        </form>
      )}
    </div>
  );
}

export default ArticleFormPage;
